import React, { useState, useEffect, useRef, FormEvent } from 'react';
import './App.css';
import logoSvg from './sinapptics.svg'; // Import the SVG file
import emailjs from '@emailjs/browser';

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesExpanded, setServicesExpanded] = useState(true);
  const [productsExpanded, setProductsExpanded] = useState(true);
  const form = useRef<HTMLFormElement>(null);

  // Function to toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const sendEmail = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!form.current) return;

    // Using send instead of sendForm to specify the recipient directly
    emailjs.send(
      'service_6t4ftbc',
      'template_f9f0hf6',
      {
        user_name: form.current.user_name.value,
        user_email: form.current.user_email.value,
        user_organization: form.current.user_organization.value,
        message: form.current.message.value,
        email: 'support@sinapptics.com' ,// Explicitly set recipient
        title: 'Contact Form Submission',
        name: form.current.user_name.value,
        company: 'Synapptics Support Team'
      },
      'ebuKeFBP7pszqQ2zL'
    )
      .then((result) => {
        console.log('Email sent successfully:', result.text);
        alert('Thank you for your message! We will get back to you soon.');
        form.current?.reset();
      }, (error) => {
        console.error('Error sending email:', error.text);
        alert('Sorry, there was a problem sending your message. Please try again later.');
      });
  };

  // Smooth scrolling for anchor links
  useEffect(() => {
    // Initialize EmailJS
    emailjs.init("ebuKeFBP7pszqQ2zL"); // ← Replace with your actual public key

    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault();
        const href = target.getAttribute('href') || '';
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          // Close mobile menu if open
          setMobileMenuOpen(false);
        }
      }
    };

    document.addEventListener('click', handleAnchorClick);

    // Image loading fallback
    const handleImageError = (e: Event) => {
      const img = e.target as HTMLImageElement;
      if (img.classList.contains('logo-svg')) {
        const fallback = document.createElement('div');
        fallback.className = 'logo-fallback';
        fallback.textContent = 'S';
        img.parentNode?.insertBefore(fallback, img);
        img.style.display = 'none';
      }
    };

    document.querySelectorAll('img[src$=".svg"]').forEach(img => {
      img.addEventListener('error', handleImageError);
    });

    return () => {
      document.removeEventListener('click', handleAnchorClick);
      document.querySelectorAll('img[src$=".svg"]').forEach(img => {
        img.removeEventListener('error', handleImageError);
      });
    };
  }, []);

  return (
    <div className="App">
      <header>
        <div className="container header-content">
          <a href="#" className="logo">
            <img src={logoSvg} alt="Sinapptics Logo" className="logo-svg" width="65" height="65" />
            Sinapptics
          </a>
          <div className="menu-toggle" id="mobile-menu" onClick={toggleMobileMenu}>
            <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
          </div>
          <nav id="nav-menu" className={mobileMenuOpen ? 'active' : ''}>
            <ul>
              <li><a href="#home">Home</a></li>
              <li><a href="#features">Features</a></li>
              <li><a href="#about">About</a></li>
              <li><a href="#contact">Contact</a></li>
            </ul>
          </nav>
        </div>
      </header>

      <section id="home" className="hero">
        <div className="container hero-content">
          <h1>Revolutionizing Healthcare with Artificial Intelligence</h1>
          <p>Sinapptics is at the forefront of integrating cutting-edge AI solutions to transform patient care, clinical decision-making, and healthcare operations. Our innovative platforms empower healthcare professionals to deliver superior care while improving efficiency.</p>
          <a href="#contact" className="cta-button">Get Started</a>
        </div>
      </section>

      <section id="features" className="section">
        <div className="container">
          <h2 className="section-title">Our Solutions</h2>
          <p className="section-subtitle">Discover how our AI-powered platforms and consulting services can transform your healthcare practice</p>
          
          {/* Services Section */}
          <div className="collapsible-section">
            <div 
              className="collapsible-header"
              onClick={() => setServicesExpanded(!servicesExpanded)}
            >
              <h3>
                <i className="fas fa-handshake"></i> Consulting Services
              </h3>
              <i className={`fas fa-chevron-${servicesExpanded ? 'up' : 'down'}`}></i>
            </div>
            <div className={`collapsible-content ${servicesExpanded ? 'expanded' : 'collapsed'}`}>
              <div className="features">
                <div className="feature-card">
                  <div className="feature-icon">
                    <i className="fas fa-lightbulb"></i>
                  </div>
                  <div className="feature-content">
                    <h3>AI/ML Strategy & Implementation</h3>
                    <p>Transform your healthcare organization with custom AI and machine learning strategies. We help you identify high-value use cases, develop proof-of-concepts, and scale AI solutions that deliver measurable ROI across clinical and operational workflows.</p>
                  </div>
                </div>
                
                <div className="feature-card">
                  <div className="feature-icon">
                    <i className="fas fa-shield-alt"></i>
                  </div>
                  <div className="feature-content">
                    <h3>AI Guardrails & Governance</h3>
                    <p>Establish robust AI governance frameworks with safety guardrails, bias detection, and compliance monitoring. We implement best practices for responsible AI deployment, including model validation, ethical guidelines, and regulatory compliance across your healthcare AI systems.</p>
                  </div>
                </div>
                
                <div className="feature-card">
                  <div className="feature-icon">
                    <i className="fas fa-cloud-upload-alt"></i>
                  </div>
                  <div className="feature-content">
                    <h3>Cloud AI Infrastructure at Scale</h3>
                    <p>Design and deploy enterprise-grade AI infrastructure on AWS, Azure, or GCP. Our experts optimize cloud architecture for performance, cost-efficiency, and scalability, implementing MLOps best practices, automated pipelines, and distributed computing for production-ready AI systems.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Products Section */}
          <div className="collapsible-section">
            <div 
              className="collapsible-header"
              onClick={() => setProductsExpanded(!productsExpanded)}
            >
              <h3>
                <i className="fas fa-cube"></i> Products
              </h3>
              <i className={`fas fa-chevron-${productsExpanded ? 'up' : 'down'}`}></i>
            </div>
            <div className={`collapsible-content ${productsExpanded ? 'expanded' : 'collapsed'}`}>
              <div className="features">
                <div className="feature-card">
                  <div className="feature-icon">
                    <i className="fas fa-file-medical"></i>
                  </div>
                  <div className="feature-content">
                    <h3>AutoAuth Pro</h3>
                    <p>Our AI-powered system streamlines prior authorization approvals by automatically generating comprehensive, compliant letters based on patient data and payer requirements, reducing approval time from days to minutes.</p>
                  </div>
                </div>
                
                <div className="feature-card">
                  <div className="feature-icon">
                    <i className="fas fa-clipboard-list"></i>
                  </div>
                  <div className="feature-content">
                    <h3>Treatment Optimization</h3>
                    <p>Leverage the power of machine learning to personalize treatment plans based on individual patient profiles, medical history, and the latest research.</p>
                  </div>
                </div>
                
                <div className="feature-card">
                  <div className="feature-icon">
                    <i className="fas fa-chart-line"></i>
                  </div>
                  <div className="feature-content">
                    <h3>PharmMetrics AI</h3>
                    <p>Our predictive analytics platform tracks critical pharmacy KPIs in real-time, identifying emerging trends and opportunities to optimize patient outcomes, ensure regulatory compliance, and enhance physician satisfaction.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="about" className="section">
        <div className="container">
          <div className="about-content">
            <div className="about-image">
              <img src={logoSvg} alt="Sinapptics Logo" className="about-svg" width="320" height="320" />
            </div>
            <div className="about-text">
              <h2>Why Choose Sinapptics?</h2>
              <p>At Sinapptics, we believe that artificial intelligence has the potential to revolutionize healthcare delivery, making it more accurate, efficient, and accessible. Our team of AI experts and healthcare professionals work together to develop solutions that address real-world clinical challenges.</p>
              <p>With a deep understanding of both the technical aspects of AI and the complexities of healthcare systems, we create intuitive platforms that seamlessly integrate into existing workflows, enhancing rather than disrupting the provider-patient relationship.</p>
              <p>Our commitment to data security and privacy ensures that all our solutions comply with HIPAA and other relevant regulations, giving you peace of mind as you embrace the future of healthcare technology.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="section contact">
        <div className="container">
          <h2 className="section-title">Get in Touch</h2>
          <p className="section-subtitle">Ready to transform your healthcare practice with AI? Contact us today!</p>
          
          <div className="contact-container">
            <div className="contact-info">
              <h2>Contact Information</h2>
              <div className="contact-method">
                <div className="contact-icon">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <div>
                  <h4>Address</h4>
                  <p>Apex, NC</p>
                </div>
              </div>
              
              <div className="contact-method">
                <div className="contact-icon">
                  <i className="fas fa-phone"></i>
                </div>
                <div>
                  <h4>Phone</h4>
                  <p>(865) 206-0407</p>
                </div>
              </div>
              
              <div className="contact-method">
                <div className="contact-icon">
                  <i className="fas fa-envelope"></i>
                </div>
                <div>
                  <h4>Email</h4>
                  <p>support@sinapptics.com</p>
                </div>
              </div>
              
              <div className="social-icons">
                <a href="https://www.linkedin.com/in/ankit-s-88aa42397" target="_blank" rel="noopener noreferrer" className="social-icon"><i className="fab fa-linkedin-in"></i></a>
                <a href="#" className="social-icon"><i className="fab fa-x-twitter"></i></a>
                <a href="#" className="social-icon"><i className="fab fa-facebook-f"></i></a>
                <a href="#" className="social-icon"><i className="fab fa-instagram"></i></a>
              </div>
            </div>
            
            <div className="contact-form">
              <form ref={form} onSubmit={sendEmail}>
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input type="text" id="name" name="user_name" className="form-control" required />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input type="email" id="email" name="user_email" className="form-control" required />
                </div>
                
                <div className="form-group">
                  <label htmlFor="organization">Organization</label>
                  <input type="text" id="organization" name="user_organization" className="form-control" />
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">How can we help?</label>
                  <textarea id="message" name="message" className="form-control" required></textarea>
                </div>
                
                <input type="hidden" name="to_email" value="support@sinapptics.com" />
                
                <button type="submit" className="submit-btn">Send Message</button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <footer>
        <div className="container">
          <div className="footer-content">
            <div className="footer-column">
              <h3>Sinapptics</h3>
              <p>Revolutionizing healthcare through artificial intelligence and machine learning technologies.</p>
              <div className="social-icons">
                <a href="#" className="social-icon"><i className="fab fa-linkedin-in"></i></a>
                <a href="#" className="social-icon"><i className="fab fa-twitter"></i></a>
                <a href="#" className="social-icon"><i className="fab fa-facebook-f"></i></a>
              </div>
            </div>
            
            <div className="footer-column">
              <h3>Solutions</h3>
              <ul>
                <li><a href="#">AutoAuth Pro</a></li>
                <li><a href="#">Treatment Planning</a></li>
                <li><a href="#">PharmMetrics AI</a></li>
                <li><a href="#">Operational Efficiency</a></li>
              </ul>
            </div>
            
            <div className="footer-column">
              <h3>Resources</h3>
              <ul>
                <li><a href="#">Blog</a></li>
                <li><a href="#">Case Studies</a></li>
                <li><a href="#">Whitepapers</a></li>
                <li><a href="#">Research</a></li>
              </ul>
            </div>
            
            <div className="footer-column">
              <h3>Company</h3>
              <ul>
                <li><a href="#">About Us</a></li>
                <li><a href="#">Careers</a></li>
                <li><a href="#">Privacy Policy</a></li>
                <li><a href="#">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} Sinapptics. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
